import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, RefreshCw, ExternalLink, Globe, Palette, Eye, Copy, CheckCircle2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const THEMES = [
  { id: "minimal-mono", name: "Minimal Mono", description: "Understated. Editorial. Zero noise." },
  { id: "editorial-serif", name: "Editorial Serif", description: "Long-form magazine layout with serif headers." },
  { id: "brutalist", name: "Brutalist", description: "Loud, uneven, deliberate. High contrast." },
  { id: "swiss-modern", name: "Swiss Modern", description: "Precise grid. Neue Haas. All-caps labels." },
];

export default function Portfolio() {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [profile, setProfile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/portfolio").then((r) => setDoc(r.data)),
      api.get("/profile").then((r) => setProfile(r.data)),
    ]);
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/portfolio/generate");
      setDoc(data);
      toast.success("Portfolio drafted by AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const tune = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/portfolio/tune", patch);
      setDoc(data);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = profile?.public_enabled && profile?.public_username
    ? `${window.location.origin}/p/${profile.public_username}`
    : null;

  const copyUrl = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Portfolio URL copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid="portfolio-page">
      <PageHeader
        eyebrow="Module 10"
        title="Portfolio Builder"
        subtitle="AI drafts a premium personal site from your profile. Tweak the theme, then share the URL with recruiters."
        actions={
          <>
            {publicUrl && (
              <Button
                variant="outline" size="sm" onClick={copyUrl}
                className="gap-2" data-testid="portfolio-copy-url"
              >
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                Copy URL
              </Button>
            )}
            <Button
              size="sm" onClick={generate} disabled={generating}
              data-testid="portfolio-generate" className="gap-2"
            >
              <RefreshCw size={14} className={generating ? "animate-spin" : ""} />
              {doc?.content ? "Regenerate" : "Draft with AI"}
            </Button>
          </>
        }
      />

      {!doc ? (
        <div className="grid md:grid-cols-3 gap-4">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      ) : !doc.content ? (
        <EmptyState
          icon={Sparkles}
          title="No portfolio yet"
          description="Click 'Draft with AI' and Claude will assemble a portfolio from your profile in seconds."
          actionLabel={generating ? "Generating…" : "Draft with AI"}
          onAction={generate}
          testId="portfolio-empty"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: settings */}
          <aside className="lg:col-span-1 space-y-4">
            <section className="bg-card border border-border rounded-xl p-5" data-testid="portfolio-visibility">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <Globe size={15} strokeWidth={1.75} /> Visibility
              </h3>
              {profile?.public_enabled && profile?.public_username ? (
                <div className="space-y-3">
                  <div className="text-xs font-mono text-muted-foreground break-all">{publicUrl}</div>
                  <a
                    href={publicUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    data-testid="portfolio-open-public"
                  >
                    Open public portfolio <ExternalLink size={13} />
                  </a>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Set a public username in <a className="text-primary hover:underline" href="/app/settings">Settings</a> to publish.
                </div>
              )}
            </section>

            <section className="bg-card border border-border rounded-xl p-5" data-testid="portfolio-theme">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <Palette size={15} strokeWidth={1.75} /> Theme
              </h3>
              <div className="space-y-2">
                {THEMES.map((t) => {
                  const active = doc.config?.theme === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`theme-${t.id}`}
                      onClick={() => tune({ theme: t.id })}
                      disabled={saving}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-foreground">{t.name}</div>
                        {active && <CheckCircle2 size={14} className="text-primary" />}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="bg-card border border-border rounded-xl p-5">
              <Label htmlFor="p-tagline">Custom tagline (optional override)</Label>
              <Input
                id="p-tagline"
                data-testid="portfolio-tagline"
                defaultValue={doc.content.tagline}
                onBlur={(e) => e.target.value !== doc.content.tagline && tune({ tagline: e.target.value })}
                className="mt-1.5"
              />
            </section>
          </aside>

          {/* Right: live preview */}
          <section className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden" data-testid="portfolio-preview">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <Eye size={14} className="text-muted-foreground" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Live preview</span>
            </div>
            <PortfolioPreview name={profile?.name || user?.name} content={doc.content} />
          </section>
        </div>
      )}
    </div>
  );
}

export function PortfolioPreview({ name, content, minimal }) {
  const c = content || {};
  return (
    <div className={`p-8 md:p-10 space-y-8 ${minimal ? "" : "bg-background"}`}>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Portfolio</div>
        <div className="font-display font-black text-3xl md:text-4xl tracking-tight text-foreground">{name || ""}</div>
        {c.tagline && (
          <div className="font-display text-lg md:text-xl text-muted-foreground mt-2 max-w-2xl">{c.tagline}</div>
        )}
      </motion.div>

      {c.hero_paragraph && (
        <p className="text-sm md:text-base text-foreground leading-relaxed max-w-2xl">{c.hero_paragraph}</p>
      )}

      {(c.highlights || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {c.highlights.map((h, i) => (
            <div key={h.label || `hl-${i}`} className="border border-border rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{h.label}</div>
              <div className="font-mono font-semibold text-2xl mt-1 text-foreground">{h.metric}</div>
              {h.description && <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{h.description}</div>}
            </div>
          ))}
        </div>
      )}

      {(c.story_sections || []).map((s, i) => (
        <section key={s.heading || `sec-${i}`} className="border-t border-border pt-6">
          <div className="font-display font-bold text-lg text-foreground mb-2 tracking-tight">{s.heading}</div>
          <p className="text-sm text-foreground leading-relaxed max-w-2xl">{s.paragraph}</p>
        </section>
      ))}

      {(c.signature_projects || []).length > 0 && (
        <section className="border-t border-border pt-6">
          <div className="font-display font-bold text-lg text-foreground mb-4 tracking-tight">Signature work</div>
          <div className="grid gap-3">
            {c.signature_projects.map((p, i) => (
              <div key={p.name || `proj-${i}`} className="border border-border rounded-lg p-4">
                <div className="font-semibold text-foreground">{p.name}</div>
                {p.description && <div className="text-sm text-muted-foreground mt-1">{p.description}</div>}
                {p.impact && (
                  <div className="mt-2 text-xs font-mono text-primary">
                    Impact · {typeof p.impact === "string" ? p.impact : JSON.stringify(p.impact)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {(c.values || []).length > 0 && (
        <section className="border-t border-border pt-6">
          <div className="font-display font-bold text-lg text-foreground mb-3 tracking-tight">What I stand for</div>
          <div className="flex flex-wrap gap-2">
            {c.values.map((v) => (
              <span key={v} className="px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-medium">
                {v}
              </span>
            ))}
          </div>
        </section>
      )}

      {c.cta_message && (
        <section className="border-t border-border pt-6">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <ArrowRight size={14} className="text-primary" />
            {c.cta_message}
          </div>
        </section>
      )}
    </div>
  );
}
